import { useState } from 'react';
import { Clock, Play } from 'lucide-react';

type Props = {
  title: string;
  description: string;
  /** Durée cible affichée (ex. "10 min"). */
  duration: string;
  /**
   * ID de la vidéo YouTube (ex. "dQw4w9WgXcQ"). Si absent, la carte affiche
   * un état « Bientôt disponible » — aucune URL n'est inventée tant que la
   * vidéo n'existe pas.
   */
  youtubeId?: string;
};

/**
 * Carte vidéo de démo — placeholder propre si `youtubeId` absent, sinon
 * embed YouTube "lite" (thumbnail cliquable, iframe chargé au clic pour ne
 * pas peser sur la performance de la page).
 */
export function DemoVideoCard({ title, description, duration, youtubeId }: Props) {
  const [activated, setActivated] = useState(false);

  return (
    <div className="flex flex-col border-2 border-charcoal-800 bg-white shadow-neobrutal-4">
      <div className="relative aspect-video overflow-hidden border-b-2 border-charcoal-800 bg-charcoal-800">
        {youtubeId ? (
          activated ? (
            <iframe
              className="absolute inset-0 h-full w-full border-0"
              src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&playsinline=1`}
              title={title}
              allow="accelerated-video-decode; autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <button
              type="button"
              onClick={() => setActivated(true)}
              aria-label={`Lire la vidéo : ${title}`}
              className="group relative flex h-full w-full items-center justify-center overflow-hidden"
            >
              <img
                src={`https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`}
                alt=""
                aria-hidden="true"
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover opacity-80 transition-opacity group-hover:opacity-100"
              />
              <span className="relative flex h-14 w-14 items-center justify-center rounded-full border-2 border-charcoal-800 bg-lime-500 shadow-neobrutal-3 transition-transform group-hover:scale-105">
                <Play className="h-6 w-6 text-charcoal-800" fill="currentColor" strokeWidth={0} />
              </span>
            </button>
          )
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-[repeating-linear-gradient(135deg,rgba(255,255,255,0.04)_0px,rgba(255,255,255,0.04)_2px,transparent_2px,transparent_10px)] px-6 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-charcoal-600 bg-charcoal-700">
              <Play className="h-5 w-5 text-charcoal-300" strokeWidth={2} />
            </span>
            <span className="inline-flex items-center gap-1.5 border-2 border-charcoal-600 bg-charcoal-900 px-2.5 py-1 font-marketing-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-lime-400">
              Bientôt disponible
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-2 p-5">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-marketing-display text-[16px] font-bold leading-[1.2] tracking-[-0.01em] text-charcoal-800">
            {title}
          </h3>
          <span className="inline-flex shrink-0 items-center gap-1 font-marketing-mono text-[11px] font-medium text-charcoal-500">
            <Clock className="h-3.5 w-3.5" strokeWidth={2.2} />
            {duration}
          </span>
        </div>
        <p className="font-marketing-body text-[14px] leading-[1.5] text-charcoal-600">
          {description}
        </p>
      </div>
    </div>
  );
}
